import { useEffect, useMemo, useState } from "react";
import { getErpAuthState, listAdminUsers } from "../erpClient";

function normalizeEmployeeRow(raw) {
  const st = parseInt(String(raw.status ?? "").trim(), 10);
  const status = Number.isFinite(st) ? st : 0;
  const roles = Array.isArray(raw.roles)
    ? raw.roles
    : raw.role != null
      ? [raw.role]
      : [];
  return {
    ua_id: raw.id ?? raw.ua_id ?? raw.user_id,
    name: String(raw.name || "").trim() || "—",
    phone: String(raw.phone || raw.mobile || "").trim(),
    email: String(raw.email || "").trim(),
    roles,
    status,
    tenant_id:
      raw.tenant_id ??
      raw.tenantId ??
      raw.employee?.tenant_id ??
      raw.tenant?.id ??
      null,
    branch_id:
      raw.branch_id ??
      raw.branchId ??
      raw.employee?.branch_id ??
      raw.branch?.id ??
      raw.default_branch_id ??
      null,
  };
}

function employeeMatchesBranchContext(emp, tenantId, branchId) {
  if (emp.status === 2) return false;
  const b = emp.branch_id;
  if (branchId && b != null && b !== "" && Number(b) !== Number(branchId)) {
    return false;
  }
  const t = emp.tenant_id;
  if (tenantId && t != null && t !== "" && Number(t) !== Number(tenantId)) {
    return false;
  }
  return true;
}

export function useHrmsEmployees(branchId) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  const tenantId = useMemo(() => {
    const u = getErpAuthState().user || {};
    const n = Number(u.tenant_id ?? u.tenantId);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, []);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!branchId) {
        setEmployees([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const res = await listAdminUsers();
        const rawFromAdmin = Array.isArray(res?.data?.users)
          ? res.data.users
          : [];
        const byId = new Map();
        for (const raw of rawFromAdmin) {
          const e = normalizeEmployeeRow(raw);
          const id = Number(e.ua_id);
          if (!Number.isFinite(id) || id <= 0) continue;
          byId.set(id, e);
        }
        const rows = Array.from(byId.values())
          .filter((e) => employeeMatchesBranchContext(e, tenantId, branchId))
          .sort((a, b) => String(a.name).localeCompare(String(b.name)));
        if (!cancelled) setEmployees(rows);
      } catch (e) {
        console.error(e);
        if (!cancelled) setEmployees([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [branchId, tenantId]);

  const selectOptions = useMemo(
    () =>
      employees.map((e) => ({
        value: Number(e.ua_id),
        label: String(e.name || "").trim() || `User #${e.ua_id}`,
        raw: e,
      })),
    [employees],
  );

  return { employees, selectOptions, loading };
}
